/**
 * Centralized persistence utilities for localStorage management.
 *
 * All persisted app state keys and types are defined here for type safety
 * and easy discoverability.
 */

// Storage key prefix to namespace our app's data
const STORAGE_PREFIX = 'antioch';

// All persisted state keys
export const StorageKeys = {
  THEME: `${STORAGE_PREFIX}:theme`,
  CURRENT_NOTEBOOK_PATH: `${STORAGE_PREFIX}:notebook:currentPath`,
  FILE_BROWSER_PATH: `${STORAGE_PREFIX}:fileBrowser:currentPath`,
  OVERLAY_POSITION: (id: string) => `${STORAGE_PREFIX}:overlay:${id}:position`,
  OVERLAY_SIZE: (id: string) => `${STORAGE_PREFIX}:overlay:${id}:size`,
  OVERLAY_SNAP: (id: string) => `${STORAGE_PREFIX}:overlay:${id}:snap`,
  OVERLAY_SNAP_DIMENSION: (id: string) => `${STORAGE_PREFIX}:overlay:${id}:snapDimension`,
  OVERLAY_MAXIMIZED: (id: string) => `${STORAGE_PREFIX}:overlay:${id}:maximized`,
  OVERLAY_VISIBILITY: `${STORAGE_PREFIX}:overlay:visibility`,
  // Dismissed maintenance banner, stored as `${workspaceId}:${scheduledShutdownAt}`
  // so a rescheduled auto-pause (new shutdown time) re-shows the notice.
  DISMISSED_MAINTENANCE: `${STORAGE_PREFIX}:maintenance:dismissed`,
  // Cross-tab state keys (managed by SharedStateProvider)
  STREAM_LOCK: `${STORAGE_PREFIX}:stream:lock`,
  CONNECTED_WORKSPACE: `${STORAGE_PREFIX}:workspace:connected`,
  SELECTED_WORKSPACE: `${STORAGE_PREFIX}:workspace:selected`,
  DEMO_MODE: `${STORAGE_PREFIX}:demo-mode`,
  // Session-scoped keys (sessionStorage, survive refresh but not new tabs)
  RUN_OUTPUT_CACHE: (workspaceId: string, filePath: string) => `${STORAGE_PREFIX}:runOutput:${workspaceId}:${filePath}`,
  RUNNING_FILE_PATH: (workspaceId: string) => `${STORAGE_PREFIX}:runningFilePath:${workspaceId}`,
} as const;

// Overlay visibility state
export interface OverlayVisibility {
  notebook?: boolean;
  docs?: boolean;
  controls?: boolean;
  ssh?: boolean;
  debug?: boolean;
  rerun?: boolean;
  antiochAgent?: boolean;
}

/**
 * Get a value from localStorage with type safety.
 *
 * Falls back to the raw string when JSON.parse fails, which handles
 * legacy values that were stored without JSON encoding (e.g. snap
 * edge strings like "left").
 */
export function getStoredValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;

  let stored: string | null;
  try {
    stored = localStorage.getItem(key);
  } catch {
    return defaultValue;
  }

  if (stored === null) return defaultValue;

  try {
    return JSON.parse(stored) as T;
  } catch {
    // Value was stored as a raw string before JSON normalization
    return stored as unknown as T;
  }
}

/**
 * Get a raw string value from localStorage.
 */
export function getStoredString(key: string): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Set a value in localStorage.
 *
 * All values are JSON-encoded so that getStoredValue can always
 * JSON.parse them back consistently.
 */
export function setStoredValue<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;

  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

/**
 * Remove a value from localStorage.
 */
export function removeStoredValue(key: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore errors
  }
}

// Convenience functions for specific state

/**
 * Get the currently saved notebook path for a workspace.
 *
 * Returns the workspace-scoped path when a workspace ID is provided.
 * Returns null when no workspace ID is given (nothing to restore).
 */
export function getCurrentNotebookPath(workspaceId: string | null): string | null {
  if (!workspaceId) return null;
  return getStoredValue<string | null>(`${StorageKeys.CURRENT_NOTEBOOK_PATH}:${workspaceId}`, null);
}

/**
 * Save the current notebook path for a workspace.
 *
 * Silently no-ops when no workspace ID is given (e.g. during startup
 * before a workspace is connected).
 */
export function setCurrentNotebookPath(workspaceId: string | null, path: string | null): void {
  if (!workspaceId) return;
  setStoredValue(`${StorageKeys.CURRENT_NOTEBOOK_PATH}:${workspaceId}`, path);
}

/**
 * Get the last directory the file browser was viewing for a workspace.
 *
 * Returns the workspace-scoped path so reopening the picker lands where
 * the user left off. Returns null when no workspace ID is given or
 * nothing has been saved yet.
 */
export function getFileBrowserPath(workspaceId: string | null): string | null {
  if (!workspaceId) return null;
  return getStoredValue<string | null>(`${StorageKeys.FILE_BROWSER_PATH}:${workspaceId}`, null);
}

/**
 * Save the directory the file browser is currently viewing for a workspace.
 *
 * The workspace root is the empty string, which is a valid value to
 * persist. Silently no-ops when no workspace ID is given.
 */
export function setFileBrowserPath(workspaceId: string | null, path: string): void {
  if (!workspaceId) return;
  setStoredValue(`${StorageKeys.FILE_BROWSER_PATH}:${workspaceId}`, path);
}

/**
 * Get a saved overlay position.
 */
export function getOverlayPosition(id: string): { x: number; y: number } | null {
  return getStoredValue(StorageKeys.OVERLAY_POSITION(id), null);
}

/**
 * Save an overlay position.
 */
export function setOverlayPosition(id: string, position: { x: number; y: number }): void {
  setStoredValue(StorageKeys.OVERLAY_POSITION(id), position);
}

/**
 * Get a saved overlay size.
 */
export function getOverlaySize(id: string): { width: number; height: number } | null {
  return getStoredValue(StorageKeys.OVERLAY_SIZE(id), null);
}

/**
 * Save an overlay size.
 */
export function setOverlaySize(id: string, size: { width: number; height: number }): void {
  setStoredValue(StorageKeys.OVERLAY_SIZE(id), size);
}

/** Snap edge for an overlay */
export type SnapEdge = 'left' | 'right' | 'bottom';

/**
 * Get a saved overlay snap edge.
 */
export function getOverlaySnap(id: string): SnapEdge | null {
  return getStoredValue<SnapEdge | null>(StorageKeys.OVERLAY_SNAP(id), null);
}

/**
 * Save an overlay snap edge (null to clear).
 */
export function setOverlaySnap(id: string, edge: SnapEdge | null): void {
  setStoredValue(StorageKeys.OVERLAY_SNAP(id), edge);
}

/**
 * Get the saved snap dimension (width for left/right, height for bottom).
 */
export function getOverlaySnapDimension(id: string): number | null {
  return getStoredValue<number | null>(StorageKeys.OVERLAY_SNAP_DIMENSION(id), null);
}

/**
 * Save the snap dimension for an overlay (null to clear).
 */
export function setOverlaySnapDimension(id: string, dimension: number | null): void {
  setStoredValue(StorageKeys.OVERLAY_SNAP_DIMENSION(id), dimension);
}

/**
 * Get the saved maximized flag for an overlay. ``false`` is the
 * default — overlays come up at their saved size unless the user
 * had explicitly maximized them.
 */
export function getOverlayMaximized(id: string): boolean {
  return getStoredValue<boolean>(StorageKeys.OVERLAY_MAXIMIZED(id), false);
}

/**
 * Save the maximized flag for an overlay.
 */
export function setOverlayMaximized(id: string, maximized: boolean): void {
  setStoredValue(StorageKeys.OVERLAY_MAXIMIZED(id), maximized);
}

/**
 * Get the saved overlay visibility states.
 */
export function getOverlayVisibility(): OverlayVisibility {
  return getStoredValue<OverlayVisibility>(StorageKeys.OVERLAY_VISIBILITY, {});
}

/**
 * Save the overlay visibility states.
 */
export function setOverlayVisibility(visibility: OverlayVisibility): void {
  setStoredValue(StorageKeys.OVERLAY_VISIBILITY, visibility);
}

/**
 * Update a single overlay's visibility.
 */
export function updateOverlayVisibility(overlayId: keyof OverlayVisibility, visible: boolean): void {
  const current = getOverlayVisibility();
  setOverlayVisibility({ ...current, [overlayId]: visible });
}

// Session-scoped persistence (sessionStorage -- survives refresh, cleared on tab close)

/**
 * Get cached run outputs for a Python file from sessionStorage.
 */
export function getRunOutputCache<T>(workspaceId: string | null, filePath: string): T[] | null {
  if (typeof window === 'undefined') return null;
  if (!workspaceId) return null;

  try {
    const stored = sessionStorage.getItem(StorageKeys.RUN_OUTPUT_CACHE(workspaceId, filePath));
    if (!stored) return null;
    return JSON.parse(stored) as T[];
  } catch {
    return null;
  }
}

/**
 * Get the path of the Python file that was running when the page last unloaded.
 *
 * Returns null if no file was running or the key has been cleared.
 */
export function getRunningFilePath(workspaceId: string | null): string | null {
  if (typeof window === 'undefined') return null;
  if (!workspaceId) return null;

  try {
    return sessionStorage.getItem(StorageKeys.RUNNING_FILE_PATH(workspaceId));
  } catch {
    return null;
  }
}

/**
 * Persist which Python file is currently executing.
 *
 * Pass null to clear the running state (e.g. when execution finishes).
 */
export function setRunningFilePath(workspaceId: string | null, filePath: string | null): void {
  if (typeof window === 'undefined') return;
  if (!workspaceId) return;

  try {
    if (filePath) {
      sessionStorage.setItem(StorageKeys.RUNNING_FILE_PATH(workspaceId), filePath);
    } else {
      sessionStorage.removeItem(StorageKeys.RUNNING_FILE_PATH(workspaceId));
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Save run outputs for a Python file to sessionStorage.
 *
 * Passing an empty array clears the cache for the given file.
 */
export function setRunOutputCache<T>(workspaceId: string | null, filePath: string, outputs: T[]): void {
  if (typeof window === 'undefined') return;
  if (!workspaceId) return;

  try {
    if (outputs.length === 0) {
      sessionStorage.removeItem(StorageKeys.RUN_OUTPUT_CACHE(workspaceId, filePath));
    } else {
      sessionStorage.setItem(StorageKeys.RUN_OUTPUT_CACHE(workspaceId, filePath), JSON.stringify(outputs));
    }
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}
